import { useState, useCallback, useEffect } from 'react';
import { sessionManager, adaptiveSessionStateManager } from '@services';

/**
 * Hook for managing study sessions
 * Integrates with adaptive session state manager for:
 * - Dynamic question group sizing
 * - Remediation injection tracking
 * - Linear progression state
 */
export function useSession(userId, courseId) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [adaptiveSessionState, setAdaptiveSessionState] = useState(null);

  /**
   * Load active session if exists
   */
  useEffect(() => {
    if (!userId || !courseId) {
      return;
    }

    const loadActiveSession = async () => {
      try {
        const activeSession = await sessionManager.resumeSession(userId, courseId);
        if (activeSession) {
          setSession(activeSession);
        }
      } catch (err) {
        console.error('Error loading active session:', err);
      }
    };

    loadActiveSession();
  }, [userId, courseId]);

  /**
   * Start new session
   */
  const startSession = useCallback(async (sectionId) => {
    if (!userId || !courseId || !sectionId) {
      throw new Error('Missing required parameters');
    }

    try {
      setLoading(true);
      setError(null);

      const newSession = await sessionManager.startSession(userId, courseId, sectionId);
      setSession(newSession);

      // Initialize adaptive session state
      const adaptiveState = await adaptiveSessionStateManager.getOrCreateSessionState(
        userId,
        courseId,
        sectionId
      );
      setAdaptiveSessionState(adaptiveState);

      console.log('[useSession] Session started with adaptive state:', {
        sessionId: newSession?.id,
        sectionId,
        adaptiveState: {
          linearProgressionPoint: adaptiveState?.linearProgressionPoint,
          groupSize: adaptiveState?.nextQuestionGroupSize,
        },
      });

      return newSession;
    } catch (err) {
      console.error('Error starting session:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [userId, courseId]);

  /**
   * Resume existing session
   */
  const resumeSession = useCallback(async () => {
    if (!userId || !courseId) {
      return null;
    }

    try {
      setLoading(true);
      setError(null);

      const existingSession = await sessionManager.resumeSession(userId, courseId);
      setSession(existingSession);

      // Also load adaptive session state if session exists
      if (existingSession && existingSession.id) {
        const adaptiveState = await adaptiveSessionStateManager.getOrCreateSessionState(
          userId,
          courseId,
          existingSession.sectionId || existingSession.linearProgressionPoint
        );
        setAdaptiveSessionState(adaptiveState);
      }

      return existingSession;
    } catch (err) {
      console.error('Error resuming session:', err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [userId, courseId]);

  /**
   * Record answer in session
   */
  const recordAnswer = useCallback(async (questionId, isCorrect, timeSpent, hintsUsed = 0) => {
    if (!session) {
      throw new Error('No active session');
    }

    try {
      const updatedSession = await sessionManager.recordSessionAnswer(
        session.id,
        questionId,
        isCorrect,
        timeSpent,
        hintsUsed
      );

      setSession(updatedSession);
      return updatedSession;
    } catch (err) {
      console.error('Error recording answer:', err);
      throw err;
    }
  }, [session]);

  /**
   * Complete session
   */
  const completeSession = useCallback(async () => {
    if (!session) {
      return null;
    }

    try {
      setLoading(true);
      const completedSession = await sessionManager.completeSession(session.id);
      setSession(completedSession);
      return completedSession;
    } catch (err) {
      console.error('Error completing session:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [session]);

  /**
   * Get session statistics
   */
  const getStats = useCallback(async () => {
    if (!session) {
      return null;
    }

    try {
      const stats = await sessionManager.getSessionStats(session.id);
      return stats;
    } catch (err) {
      console.error('Error getting session stats:', err);
      return null;
    }
  }, [session]);

  return {
    session,
    adaptiveSessionState,
    loading,
    error,
    startSession,
    resumeSession,
    recordAnswer,
    completeSession,
    getStats,
  };
}

/**
 * Hook for getting session history
 */
export function useSessionHistory(userId, courseId, limit = 10) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId || !courseId) {
      setLoading(false);
      return;
    }

    const loadHistory = async () => {
      try {
        setLoading(true);
        setError(null);

        const sessions = await sessionManager.getSessionHistory(userId, courseId, limit);
        setHistory(sessions);
      } catch (err) {
        console.error('Error loading session history:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadHistory();
  }, [userId, courseId, limit]);

  return { history, loading, error };
}

/**
 * Hook for warm-up questions
 */
export function useWarmUpQuestions(userId, courseId, currentSectionId) {
  const [warmUpQuestions, setWarmUpQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadWarmUpQuestions = useCallback(async () => {
    if (!userId || !courseId || !currentSectionId) {
      return [];
    }

    try {
      setLoading(true);
      setError(null);

      const questions = await sessionManager.getResumeWarmUpQuestions(
        userId,
        courseId,
        currentSectionId
      );

      setWarmUpQuestions(questions);
      return questions;
    } catch (err) {
      console.error('Error loading warm-up questions:', err);
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  }, [userId, courseId, currentSectionId]);

  return {
    warmUpQuestions,
    loading,
    error,
    loadWarmUpQuestions,
  };
}
